"use client";

import { Plus } from "lucide-react";
import { usePathname } from "next/navigation";
import { PayloadLink } from "@/components/shared/PayloadLink";
import { useNavCollapsed } from "./ScrollableHeader";
import { cn } from "@/lib/utils";
import { isNavItemActive, type RightNavItem as RightNavItemConfig } from "@/lib/navigation-utils";

interface RightNavProps {
  items?: RightNavItemConfig[];
}

export function RightNav({ items }: RightNavProps) {
  if (!items || items.length === 0) return null;

  return (
    <nav className="hidden xl:flex xl:items-center xl:justify-self-end xl:gap-4 3xl:gap-7 transition-all duration-300">
      {items.map((item) => (
        <RightNavItem key={item.id} item={item} />
      ))}
    </nav>
  );
}

function RightNavItem({ item }: { item: RightNavItemConfig }) {
  const collapsed = useNavCollapsed();
  const pathname = usePathname() ?? "";
  const isActive = isNavItemActive(pathname, item);

  return (
    <div className="relative group">
      <PayloadLink
        type={item.type}
        page={item.page}
        url={item.url}
        newTab={item.newTab}
        className={cn("px-1 transition-all duration-300 block", collapsed ? "py-2" : "py-4")}
      >
        <div className={cn("flex items-center transition-all duration-300", collapsed ? "gap-1" : "gap-3")}>
          {/* Always-on + glyph — right nav items don't carry their own icons. */}
          <div className="relative flex-shrink-0 h-6 w-6 flex items-center justify-center">
            <Plus
              className="h-5 w-5 text-white/80 transition-transform duration-300 group-hover:rotate-90"
              aria-hidden="true"
            />
          </div>
          <div className="flex flex-col">
            <span
              className={cn(
                "font-semibold text-lg 3xl:text-[20px] leading-tight transition-colors group-hover:text-brand",
                isActive ? "text-brand" : "text-white",
              )}
            >
              {item.label}
            </span>
            {item.description && (
              <span className={cn("text-white/60 text-[10px] leading-tight overflow-hidden transition-all duration-300", collapsed ? "opacity-0 max-h-0 max-w-0 mt-0" : "opacity-100 max-h-6 max-w-[16ch] 3xl:max-w-[18ch] mt-0.5")}>
                {item.description}
              </span>
            )}
          </div>
        </div>
      </PayloadLink>
    </div>
  );
}
